<template>
  <HeaderBar />

  <ButtonBackContainer>
    <ButtonBack :fallback="{ name: 'userList' }">{{ t('back') }}</ButtonBack>
    <AuditLogButton :entity-type="AuditLogEntityType.USER" :entity-id="userId" />
  </ButtonBackContainer>

  <section class="carvia-container">
    <UserForm
      v-if="(user || userNew) && stations && me"
      :user="user"
      :me="me"
      :stations="stations"
      :is-loading="isLoading"
      :is-fetching="isFetching"
      @on-submit="onSubmit"
      @refetch="refetch"
    />
  </section>

  <section v-if="user" class="carvia-container space-y-4">
    <h2>{{ t('documents') }}</h2>
    <UserDocuments :user="user" />
  </section>

  <section v-if="user" class="carvia-container space-y-4">
    <h2>{{ t('more') }}</h2>
    <LabeledText v-if="supportsWelcomeMail" :label="t('commissionAgent')">
      <CVButton :is-loading="isSendingWelcome" @click="resendWelcomeMail">
        {{ t('resendWelcomeMail') }}
      </CVButton>
    </LabeledText>
    <div v-if="$can('manage', 'Newsletter')">
      <LabeledText :label="t('newsletterBlacklist')">
        <div v-if="newsletterBlacklist" class="flex flex-col gap-2">
          <span
            class="self-start rounded-sm px-2 text-sm"
            :class="{
              'bg-green-50 text-green-800':
                !newsletterBlacklist.isBlacklisted && newsletterBlacklist.isEmailOnNewsletterList,
              'bg-red-50 text-red-700':
                newsletterBlacklist.isBlacklisted && newsletterBlacklist.isEmailOnNewsletterList,
              'bg-obsidian-200 text-obsidian-800': !newsletterBlacklist.isEmailOnNewsletterList,
            }"
          >
            <span v-if="!newsletterBlacklist.isEmailOnNewsletterList">{{ t('isNotOnList') }}</span>
            <span v-else>{{
              newsletterBlacklist.isBlacklisted ? t('isOnBlacklist') : t('isNotOnBlacklist')
            }}</span>
          </span>
          <CVButton
            v-if="newsletterBlacklist.isEmailOnNewsletterList"
            :is-loading="isSavingNewsletterBlacklist"
            :disabled="isLoadingNewsletterBlacklisted"
            :variant="!newsletterBlacklist.isBlacklisted ? 'danger' : 'secondary'"
            @click="toggleNewsletterBlacklist"
          >
            {{ newsletterBlacklist.isBlacklisted ? t('removeFromBlacklist') : t('addToBlacklist') }}
          </CVButton>
        </div>
        <div v-else>
          <Spinner class="size-4" />
        </div>
      </LabeledText>
    </div>

    <LabeledText v-if="canDeletePermanently" :label="t('customerAccount')">
      <CVButton variant="danger" :is-loading="isDeleting" @click="handleDeletePermanently">
        {{ t('deletePermanently') }}
      </CVButton>
    </LabeledText>

    <div v-if="userHasProvision && userProvision.data.value">
      <Divider />
      <h2 class="my-4">{{ t('provisions') }}</h2>
      {{
        t('provisionsTime', {
          start: formatDate(userProvision.data.value.startDate, getDateFormat()),
          end: formatDate(userProvision.data.value.endDate, getDateFormat()),
        })
      }}
      <br />
      {{
        t('provisionsContent', {
          handovers: userProvision.data.value.handovers,
          returns: userProvision.data.value.returns,
          detailings: userProvision.data.value.detailings,
          extras: formatCurrency(userProvision.data.value.extrasProvisions),
          ratings: formatCurrency(userProvision.data.value.ratingProvisions),
        })
      }}
    </div>

    <div v-if="isApiKeysSectionVisible" class="mt-6">
      <Divider />
      <ApiKeysSection :user-id="userId" />
    </div>
  </section>

  <section v-if="user" class="carvia-container flex flex-col gap-4">
    <h2>{{ t('bookings') }}</h2>
    <UserBookings :user-id="userId" />
  </section>
</template>

<script lang="ts" setup>
import ButtonBackContainer from '@/components/ButtonBackContainer.vue';
import ButtonBack from '@/components/buttons/ButtonBack.vue';
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import type { CreateUserDto, UpdateUserDto } from '@/entities/auth/user.entity';
import { useI18n } from 'vue-i18n';
import { useStations } from '@/queries/use-stations';
import {
  useCreateUser,
  useDeleteUserPermanently,
  useMe,
  useUpdateUser,
  useUser,
} from '@/queries/use-users';
import { useUiStore } from '@/stores/ui.store';
import { computed, ref, watchEffect } from 'vue';
import { useRoute } from 'vue-router';
import UserForm from './UserForm.vue';
import router from '@/router';
import { UserRole } from '@/entities/auth/user-role.enum';
import AuditLogButton from '@/components/audit-log/AuditLogButton.vue';
import { AuditLogEntityType } from '@carvia/ros-client-types';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import { useYearlyAgentProvisionSummary } from '@/queries/use-agent-provision-summary';
import Divider from '@/components/Divider.vue';
import UserBookings from './UserBookings.vue';
import LabeledText from '@/components/LabeledText.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import Spinner from '@/components/icons/Spinner.vue';
import {
  useGetNewsletterBlacklisted,
  useSetNewsletterBlacklisted,
} from '@/queries/use-newsletter.ts';
import { formatDate } from '@/hooks/use-formatted-date.ts';
import { getDateFormat } from '@/hooks/use-date-format.ts';
import { formatCurrency } from '@/utils/format-numbers.ts';
import { usersService } from '@/api/users.service';
import { AgencyType } from '@/entities/auth/agency-type.enum';
import { Alert } from '@/services/alert.service';
import ApiKeysSection from './ApiKeysSection.vue';
import { useAbility } from '@casl/vue';
import UserDocuments from './UserDocuments.vue';

const { t } = useI18n();
const uiStore = useUiStore();
const route = useRoute();
const userId = route.params.id as string;
const userNew = ref(userId === 'new');
const userFetchingEnabled = computed(() => !userNew.value);

const ability = useAbility();

const { data: me, isLoading: isLoadingMe } = useMe();
const { data: user, refetch, isFetching } = useUser(userId, userFetchingEnabled);
const { mutateAsync: createUser, isPending: isSaving } = useCreateUser();
const { mutateAsync: updateUser, isPending: isUpdating } = useUpdateUser();

const { data: stationsData, isFetching: isLoadingStations } = useStations(500);
const stations = useFlattenPaginatedData(stationsData);

const isLoading = computed(
  () => isSaving.value || isUpdating.value || isLoadingStations.value || isLoadingMe.value,
);

const userHasProvision = computed(
  () => !!user.value?.role && [UserRole.HUB_AGENT, UserRole.HUB_MANAGER].includes(user.value?.role),
);
const userProvision = useYearlyAgentProvisionSummary(userId, userHasProvision);
const supportsWelcomeMail = computed(
  () =>
    (user.value?.role === UserRole.AGENCY &&
      user.value?.agencyType === AgencyType.AGENCY_ON_COMMISSION_BASIS) ||
    user.value?.role === UserRole.COMPANY,
);
const isBrokerAgent = computed(
  () => user.value?.role === UserRole.AGENCY && user.value?.agencyType === AgencyType.BROKER,
);
const isApiKeysSectionVisible = computed(() => {
  return (
    ([UserRole.ADMIN, UserRole.OPERATOR].includes(user.value?.role) || isBrokerAgent.value) &&
    ability.can('read', 'ApiKey')
  );
});

const isSendingWelcome = ref(false);
const resendWelcomeMail = async () => {
  if (!user.value) return;
  const result = await Alert.fire({
    titleText: t('resendWelcomeMail'),
    text: t('resendWelcomeMailConfirm'),
    icon: 'question',
    showDenyButton: true,
    confirmButtonText: t('send'),
    denyButtonText: t('cancel'),
  });
  if (!result.isConfirmed) return;
  try {
    isSendingWelcome.value = true;
    await usersService.resendWelcomeMail(user.value.id);
    await Alert.fire({ icon: 'success', titleText: t('mailSent') });
  } finally {
    isSendingWelcome.value = false;
  }
};

const canDeletePermanently = computed(
  () => user.value?.role === UserRole.CUSTOMER && ability.can('delete', 'User'),
);
const { mutateAsync: deleteUserPermanently, isPending: isDeleting } = useDeleteUserPermanently();
const handleDeletePermanently = async () => {
  if (!user.value) return;
  const result = await Alert.fire({
    titleText: t('deletePermanently'),
    text: t('deletePermanentlyConfirm'),
    icon: 'warning',
    showDenyButton: true,
    confirmButtonText: t('delete'),
    denyButtonText: t('cancel'),
  });
  if (!result.isConfirmed) return;
  await deleteUserPermanently(user.value.id);
  router.push({ name: 'userList' });
};

watchEffect(() => {
  if (user.value) {
    const { firstName, lastName, email } = user.value;
    uiStore.setHeaderTitle(t('editUser'), firstName + ' ' + lastName, email);
  } else {
    uiStore.setHeaderTitle(t('createUser'));
  }
});

const onSubmit = async (formValues: UpdateUserDto) => {
  const { role: _role, ...formValuesWithoutRole } = formValues;
  const authUserIsAdmin = me.value?.role === UserRole.ADMIN;
  if (!userNew.value) {
    await updateUser({
      id: userId,
      user: authUserIsAdmin ? formValues : formValuesWithoutRole,
    });
  } else {
    await createUser(formValues as CreateUserDto);
    router.push({ name: 'userList' });
  }
};

const { mutateAsync: setNewsletterBlacklisted, isPending: isSavingNewsletterBlacklist } =
  useSetNewsletterBlacklisted();
const {
  data: newsletterBlacklist,
  refetch: refetchNewsletterBlacklisted,
  isLoading: isLoadingNewsletterBlacklisted,
} = useGetNewsletterBlacklisted(computed(() => user.value?.email ?? ''));

const toggleNewsletterBlacklist = async () => {
  if (!user.value?.email) return;
  const newBlacklistedStatus = !newsletterBlacklist.value?.isBlacklisted;
  await setNewsletterBlacklisted({
    email: user.value.email,
    blacklisted: newBlacklistedStatus,
  });
  await refetchNewsletterBlacklisted();
};
</script>

<i18n lang="json">
{
  "en": {
    "editUser": "Edit User",
    "createUser": "Create User",
    "provisions": "Provisions",
    "provisionsTime": "Provisions in the period from {start} to {end}:",
    "provisionsContent": "{handovers} handovers, {returns} returns, {detailings} detailings, {extras} extras, {ratings} ratings",
    "bookings": "Bookings",
    "newsletterBlacklist": "Newsletter Blacklist",
    "addToBlacklist": "Add to Blacklist",
    "removeFromBlacklist": "Remove from Blacklist",
    "isOnBlacklist": "Is on Blacklist",
    "isNotOnBlacklist": "Is not on Blacklist",
    "isNotOnList": "Not on Newsletter List",
    "more": "More",
    "commissionAgent": "Commission Agent",
    "resendWelcomeMail": "Resend welcome email",
    "resendWelcomeMailConfirm": "Really resend the welcome email to this commission agency?",
    "send": "Send",
    "cancel": "Cancel",
    "mailSent": "Mail sent",
    "documents": "Documents",
    "customerAccount": "Customer account",
    "deletePermanently": "Delete permanently",
    "deletePermanentlyConfirm": "The account and its personal data will be deleted irreversibly. Bookings and invoices keep the customer data stored on them.",
    "delete": "Delete"
  },
  "de": {
    "editUser": "Benutzer bearbeiten",
    "createUser": "Benutzer erstellen",
    "provisions": "Provisionen",
    "provisionsTime": "Provisionen im Zeitraum von {start} bis {end}:",
    "provisionsContent": "{handovers} Übergaben, {returns} Rückgaben, {detailings} Aufbereitungen, {extras} Extras, {ratings} 5★-Provisionen",
    "bookings": "Buchungen",
    "newsletterBlacklist": "Newsletter-Blacklist",
    "addToBlacklist": "Zur Blacklist hinzufügen",
    "removeFromBlacklist": "Von Blacklist entfernen",
    "isOnBlacklist": "Ist auf Blacklist",
    "isNotOnBlacklist": "Ist nicht auf Blacklist",
    "isNotOnList": "Nicht auf Newsletter-Liste",
    "more": "Weiteres",
    "commissionAgent": "Provisionspartner",
    "resendWelcomeMail": "Willkommensmail erneut senden",
    "resendWelcomeMailConfirm": "Willkommensmail wirklich erneut an diesen User senden?",
    "send": "Senden",
    "cancel": "Abbrechen",
    "mailSent": "E-Mail gesendet",
    "documents": "Dokumente",
    "customerAccount": "Kundenkonto",
    "deletePermanently": "Endgültig löschen",
    "deletePermanentlyConfirm": "Das Konto und seine personenbezogenen Daten werden unwiderruflich gelöscht. Buchungen und Rechnungen behalten die dort hinterlegten Kundendaten.",
    "delete": "Löschen"
  },
  "es": {
    "editUser": "Editar usuario",
    "createUser": "Crear usuario",
    "provisions": "Comisiones",
    "provisionsTime": "Comisiones en el periodo del {start} al {end}:",
    "provisionsContent": "{handovers} entregas, {returns} devoluciones, {detailings} detailing, {extras} extras, {ratings} valoraciones 5★",
    "bookings": "Reservas",
    "newsletterBlacklist": "Lista de exclusión del boletín",
    "addToBlacklist": "Añadir a la lista de exclusión",
    "removeFromBlacklist": "Quitar de la lista de exclusión",
    "isOnBlacklist": "Está en la lista de exclusión",
    "isNotOnBlacklist": "No está en la lista de exclusión",
    "isNotOnList": "No está en la lista del boletín",
    "more": "Más",
    "commissionAgent": "Agente de comisiones",
    "resendWelcomeMail": "Reenviar correo de bienvenida",
    "resendWelcomeMailConfirm": "¿Reenviar el correo de bienvenida a este agente de comisiones?",
    "send": "Enviar",
    "cancel": "Cancelar",
    "mailSent": "Correo enviado",
    "documents": "Documentos",
    "customerAccount": "Cuenta de cliente",
    "deletePermanently": "Eliminar definitivamente",
    "deletePermanentlyConfirm": "La cuenta y sus datos personales se eliminarán de forma irreversible. Las reservas y facturas conservan los datos del cliente guardados en ellas.",
    "delete": "Eliminar"
  }
}
</i18n>
